import { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import dayjs from 'dayjs';
import { Box, Button, Typography, Divider, Stack, Grid } from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ListOutlinedIcon from '@mui/icons-material/ListOutlined';

import Yup from '../../../../utils/yup';
import { AutoCompleteSelectUI, DialogUI, FileUploadUI, FormErrorUI, FormLabelUI, SwitchUI } from '../../../Interface';
import TodoDialogFields from './TodoDialogFields';
import TodoDialogSideItem from './TodoDialogSideItem';
import useUsers from '../../../../context/UsersContext';
import simpletaskService from '../../../../service/simpletask.service';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import workspaceService from '../../../../service/CRM/workspace.service';
import CommentMessageField from '../../../Comment/CommentMessageField';

const TodoDialog = ({ dialogData, handleClose, autoCompleteOptions, autoCompleteData, setRefetch }) => {
  const { user } = useUsers();
  const { open, projectId, data } = dialogData;
  const [commentText, setCommentText] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState([]);

  const defVisibleFields = {
    simpleTaskRespPersonlist: false,
    checkList: false,
  };
  const [visibleFields, setVisibleFields] = useState(defVisibleFields);

  const defValues = {
    simpleTaskName: '',
    resposiblePersonId: null,
    simpleTaskTypeId: null,
    statusId: null,
    startDate: dayjs(new Date()),
    endDate: dayjs(new Date()).add(1, 'hour'),
    duration: 1,
    simpleTaskRespPersonlist: [],
    simpleTaskAttachment: [],
  };
  const [initValues, setInitValues] = useState(defValues);

  const isEdit = Boolean(data?.simpleTaskId);

  useEffect(() => {
    if (!open) {
      setVisibleFields(defVisibleFields);
      setCommentText('');
      setMentionedUserIds([]);
      return;
    }

    if (isEdit) {
      valuesToForm();
    } else {
      setInitValues({
        ...defValues,
        resposiblePersonId: user?.userId,
        simpleTaskTypeId: autoCompleteData?.taskType?.defaultValue,
        statusId: autoCompleteData?.taskStatus?.defaultValue,
      });
    }
  }, [open, data]);

  const validationSchema = Yup.object().shape({
    simpleTaskName: Yup.string().required(),
    startDate: Yup.string().required(),
    endDate: Yup.string().required(),
    simpleTaskTypeId: Yup.string().required(),
    statusId: Yup.string().required(),
    resposiblePersonId: Yup.string().required(),
    simpleTaskRespPersonlist: Yup.array().of(
      Yup.object().shape({
        userId: Yup.string().required(),
        roleName: Yup.string().required(),
      })
    ),
  });

  const valuesToForm = () => {
    const {
      simpleTaskId,
      simpleTaskName,
      resposiblePersonId,
      simpleTaskTypeId,
      startDate,
      endDate,
      duration,
      simpleTaskRespPersonlist,
      simpleTaskAttachment,
      statusId,
    } = data;

    setInitValues({
      simpleTaskId,
      simpleTaskName,
      resposiblePersonId,
      simpleTaskTypeId,
      startDate: dayjs(startDate),
      endDate: dayjs(endDate),
      duration,
      simpleTaskRespPersonlist,
      simpleTaskAttachment,
      statusId,
    });

    setVisibleFields({
      simpleTaskRespPersonlist: simpleTaskRespPersonlist.length > 0,
      checkList: false,
    });
  };

  const handleSubmit = (values) => {
    if (isEdit) {
      handleUpdate(values);
    } else {
      handleCreate(values);
    }
  };

  const handleCreate = (values) => {
    const priorId = autoCompleteData?.taskPrio?.defaultValue;

    if (!priorId) {
      addNotification({
        content: 'Nem található alapértelmezett prioritás!',
        type: NotificationType.WARNING,
      });
      return;
    }

    const insertObject = {
      ...values,
      duration: parseFloat(values.duration),
      startDate: dayjs(values.startDate).toISOString(),
      endDate: dayjs(values.endDate).toISOString(),
      createdBy: user.userId,
      userId: user.userId,
      priorId,
      projectId,
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.simpleTaskAttachment) {
      for (const file of insertObject.simpleTaskAttachment) {
        formData.append('files', file);
      }
    }
    simpletaskService.createSimpleTask(formData).then((data) => {
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
      setRefetch(true);
      handleClose();
    });
  };

  const handleUpdate = (values) => {
    const updData = {
      ...values,
      duration: parseFloat(values.duration),
      startDate: dayjs(values.startDate).toISOString(),
      endDate: dayjs(values.endDate).toISOString(),
      ...(commentText.trim() && {
        comment: {
          message: commentText,
          mentionedUserIds,
        },
      }),
    };

    updData.existingFiles = updData.simpleTaskAttachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(updData));

    if (updData.simpleTaskAttachment) {
      for (const file of updData.simpleTaskAttachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }
    workspaceService.updateTask(formData).then((simpleTask) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setRefetch(true);
      handleClose();
    });
  };

  const toggleField = (key, values) => {
    setVisibleFields((prev) => {
      const isVisible = prev[key];
      const value = values[key];

      const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value);

      if (isVisible && hasValue) {
        return prev;
      }

      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  };

  const sideItems = [
    {
      key: 'simpleTaskRespPersonlist',
      label: 'Érintettek',
      icon: <PeopleAltOutlinedIcon />,
    },
    {
      key: 'checkList',
      label: 'Lista',
      icon: <ListOutlinedIcon />,
    },
  ];

  return (
    <DialogUI
      open={open}
      onClose={handleClose}
      headerContent={isEdit ? 'Teendő szerkesztése' : 'Új teendő hozzáadása'}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '800px',
          },
        },
        '& .MuiDialogContent-root': {
          px: 2,
          pb: 0,
        },
      }}
    >
      <Box mt={2}>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, errors, validateField }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} md={9}>
                  <TodoDialogFields
                    toggleField={toggleField}
                    visibleFields={visibleFields}
                    autoCompleteOptions={autoCompleteOptions}
                  />

                  <FileUploadUI
                    rootSx={{ m: 0, mt: 2 }}
                    value={values.simpleTaskAttachment}
                    onFilesChange={(files) =>
                      setFieldValue(
                        'simpleTaskAttachment',
                        files.map(({ file }) => file)
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button type="submit" fullWidth variant="contained" sx={{ mb: 3 }}>
                    {isEdit ? 'Mentés' : 'Hozzáadás'}
                  </Button>
                  <Field
                    type="autocomplete"
                    name="statusId"
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text="Státusz" />}
                    variant="outlined"
                    size="small"
                    helperText={<FormErrorUI message={errors.statusId} />}
                    onChange={(_e, newVal) => {
                      setFieldValue('statusId', newVal?.value ?? null).then(() => {
                        validateField('statusId');
                      });
                    }}
                    InputLabelProps={{ shrink: values.statusId !== '' }}
                    selectedValue={values.statusId}
                    dataset={autoCompleteOptions?.taskStatus}
                  />
                  <Typography sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>Hozzáadás</Typography>
                  {sideItems.map((item) => (
                    <TodoDialogSideItem
                      key={item.key}
                      item={item}
                      toggleField={toggleField}
                      visibleFields={visibleFields}
                    />
                  ))}
                </Grid>
              </Grid>
              {isEdit && (
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography sx={{ fontWeight: 'bold' }}>Aktivitás</Typography>
                    <SwitchUI defaultChecked label={<FormLabelUI text="Előzmények" />} />
                  </Stack>
                  <CommentMessageField
                    users={autoCompleteData.user}
                    value={commentText}
                    autoFocus={false}
                    onChange={(e) => setCommentText(e.target.value)}
                    mentionedUserState={{
                      mentionedUserIds,
                      setMentionedUserIds,
                    }}
                  />
                </Box>
              )}
            </Form>
          )}
        </Formik>
      </Box>
    </DialogUI>
  );
};

export default TodoDialog;
